import type { CSSProperties } from 'react';

// The chevron is a sibling element rather than a background-image SVG:
// background chevrons render too faint at 12px on macOS.
export const loopInputStyle: CSSProperties = {
  padding: '8px 12px',
  border: '1px solid var(--loop-neutral-300)',
  borderRadius: 'var(--loop-radius-sm)',
  fontSize: '0.875rem',
  color: 'var(--loop-neutral-1100)',
  background: 'var(--loop-neutral-0)',
  height: '36px',
  boxSizing: 'border-box',
  // Native form controls do not inherit font-family from the page, so without
  // this a select renders in the OS UI font instead of the design-system face.
  // The omission is invisible on macOS (the token falls back to -apple-system
  // while no webfont is loaded), which is how two of the three copies of this
  // block drifted without anyone noticing.
  fontFamily: 'var(--loop-font-family-body)',
};

const selectStyle: CSSProperties = {
  ...loopInputStyle,
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  cursor: 'pointer',
  paddingRight: '32px',
};

const wrapStyle: CSSProperties = { position: 'relative', display: 'inline-block' };

const chevronStyle: CSSProperties = {
  position: 'absolute',
  right: 10,
  top: '50%',
  transform: 'translateY(-50%)',
  pointerEvents: 'none',
  color: 'var(--loop-neutral-900)',
  display: 'flex',
  alignItems: 'center',
};

export const fieldLabelStyle: CSSProperties = {
  display: 'block',
  marginBottom: 4,
  color: 'var(--loop-neutral-600)',
  fontSize: '0.75rem',
};

export interface LoopSelectOption { value: string; label: string; }

interface LoopSelectProps {
  id: string;
  label: string;
  value: string;
  options: LoopSelectOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function LoopSelect({ id, label, value, options, onChange, disabled }: LoopSelectProps) {
  return (
    <div>
      <label htmlFor={id} style={fieldLabelStyle}>{label}</label>
      <div style={wrapStyle}>
        <select
          id={id}
          value={value}
          onChange={e => onChange(e.target.value)}
          style={selectStyle}
          disabled={disabled}
        >
          {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span style={chevronStyle} aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </div>
    </div>
  );
}
